/**
 * The controls around a list — list engine v2's toolbar.
 *
 * Two groups, and the split is the point.
 *
 * `ListViewControls` is the Filter button and nothing else. v2 moves Group,
 * Sort, Fields and Σ behind the gear on the right, because the column header
 * now does all four and a toolbar row of buttons nobody presses twice is a
 * toolbar row of noise. Filter stays on the left because it is the one that is
 * reached for constantly, and because the chips beside it are the answer to
 * "what am I looking at?".
 *
 * `ListTools` is everything that acts on the list as a whole: import, export,
 * the gear, and the screen's primary action — which v2 moves out of the page
 * bar, so the button that makes a record sits with the tools rather than with
 * the title.
 */
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'
import {
  AGGREGATE_FUNCTIONS, datePeriod, describeCondition, describeFilter, isFilterGroup,
  isRelatedCondition, isToken, labelFor,
  type AggregateFunction, type FilterCondition, type FilterNode,
  type FilterOperator,
} from '@rowted/shared'
import { Popover } from './overlays.js'
import { Button, SectionLabel, Segmented } from './primitives.js'
import { FieldsPanel } from './list-fields.js'
import {
  ControlPanel, PANEL_WIDTH, PanelRow, PanelSection, panelFacts, usePanelSearch,
  type GearPanel,
} from './control-panel.js'
import { MenuSelect } from './menu-select.js'
import { filterLabels } from './list-filter.js'
import { QuickFilter } from './quick-filter.js'
import { PanelSheet, SheetButton } from './panel-sheet.js'
import {
  IconCheck, IconChevronRight, IconClock, IconClose, IconExport, IconFields, IconFilter,
  IconGroup, IconLock, IconSettings, IconSmartImport, IconSigma, IconSort,
} from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { formatNumber } from '../lib/format.js'
import { usePhone } from '../lib/use-narrow.js'
import type { ListFieldDef, UseListResult } from '../lib/use-list.js'

/**
 * How big a toolbar glyph is, and the only two answers.
 *
 * A button with its word beside it is read by the word; the icon is a marker
 * in front of it, and 15 is what the mockups draw. A button with no word is
 * read by the glyph alone — it *is* the control — so it gets 18, the icon
 * kit's own default, centred in the 36px square.
 *
 * Stated here rather than tuned per button, because tuning is what produced a
 * row where the gear was 17, the funnel 15, and neither matched. On a phone
 * every one of these is wordless, which is why they must agree.
 */
export const TOOLBAR_GLYPH = 15
export const SQUARE_GLYPH = 18

function ControlButton({
  label, Icon, badge, open, onClick, wordless,
}: {
  label: string
  /** The component, not an element: this decides the size, not the caller. */
  Icon: (props: { size?: number }) => ReactElement
  badge?: string
  open: boolean
  onClick: () => void
  /** Narrow: the glyph carries it, and the word goes to the tooltip. */
  wordless?: boolean
}) {
  return (
    <Button
      onClick={onClick}
      aria-expanded={open}
      aria-label={wordless ? label : undefined}
      title={label}
      size="toolbar"
      variant={open ? 'pressed' : 'default'}
      /*
       * Icon-only means a square, not a narrower rectangle. Every control on
       * a phone's list toolbar — Filter, the gear, the green one, and the
       * New card tile in the strip above — is the same 36px box, so the row
       * reads as a row of buttons rather than four different-sized things.
       *
       * `square` rather than a class: written here it was `w-9 gap-0 px-0`
       * beside the size's own `px-3`, and `cn` joins, so the button kept 12px
       * of padding each side and squashed its 15px glyph into a 10px box.
       */
      square={wordless}
      className={cn('gap-1.5', wordless && 'gap-0', wordless && badge && 'relative')}
    >
      <Icon size={wordless ? SQUARE_GLYPH : TOOLBAR_GLYPH} />
      {!wordless && label}

      {/*
        * **On a square button the count goes over the corner**, not inside
        * it (board 7's numbers: 18px, `-6px` out on both sides, a 2px ring in
        * the page background so it reads as a token sitting on the button
        * rather than a shape cut into it).
        *
        * Inline, it was a second flex child of a 36px box: the funnel was
        * pushed off centre to make room and the button grew past its square.
        * One control cannot be both icon-only and two things wide.
        *
        * Beside a word it stays inline, because there the button is a
        * rectangle sized by its label and a badge in the corner would hang
        * off nothing.
        */}
      {badge && (wordless
        ? (
          <span className={cn('tnum absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px]',
            'items-center justify-center rounded-full bg-accent px-[5px]',
            'text-[11px] font-bold text-white ring-2 ring-bg')}
          >
            {badge}
          </span>
        )
        : (
          <span className="tnum rounded-full bg-accent-soft px-1.5 text-[11px] font-semibold text-accent">
            {badge}
          </span>
        ))}
    </Button>
  )
}

export function ListViewControls({
  list, startField, onOpenChange, onAdvanced, wordless,
}: {
  list: UseListResult
  /** A column a header's "Filter by…" sent here; it opens the popover on it. */
  startField?: string | null
  onOpenChange?: (open: boolean) => void
  onAdvanced: () => void
  wordless?: boolean
}) {
  const [open, setOpen] = useState(false)
  const conditions = list.view.filter?.conditions ?? []

  /*
   * A header's "Filter by Status…", and a click on a toolbar chip, both
   * arrive as `startField`. Opening on it is the whole of what they do.
   */
  useEffect(() => { if (startField) setOpen(true) }, [startField])

  const close = () => { setOpen(false); onOpenChange?.(false) }

  /*
   * Anchored, so the panel is placed rather than hung.
   *
   * Without this the popover took its fallback — `absolute left-0`, six
   * pixels under the button — and the widest panel in the product hung off
   * whichever edge the button happened to be near. On a 390px phone the
   * Filter button sits 250px along, so 360px of panel ended 220px past the
   * right-hand edge of the screen. `placeFloating` is the one thing in the
   * app that knows where the edges are; giving it the button is how a menu
   * gets to use it.
   */
  const control = useRef<HTMLDivElement>(null)

  return (
    <div ref={control} className="relative">
      <ControlButton
        label="Filter"
        Icon={IconFilter}
        badge={conditions.length > 0 ? String(conditions.length) : undefined}
        open={open}
        wordless={wordless}
        onClick={() => (open ? close() : setOpen(true))}
      />
      <QuickFilter
        list={list}
        open={open}
        onClose={close}
        anchor={control.current}
        focusField={startField}
        onAdvanced={() => { close(); onAdvanced() }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// The gear's four panels
// ---------------------------------------------------------------------------

/**
 * Group stays open when you pick, the way Sort does.
 *
 * There is no "No grouping" row either: nothing selected *is* no grouping, and
 * Ungroup in the footer says it for every panel in the same place. A row that
 * means "undo the thing this menu does" sitting at the top of the list of
 * things this menu does is a row people click by accident.
 */
function GroupPicker(
  { list, onBack, bare }: { list: UseListResult; onBack: () => void; bare?: boolean },
) {
  const facts = panelFacts('group', list)
  const { query, setQuery, matches } = usePanelSearch()
  const groupable = list.fields.filter((field) => field.groupable).filter(matches)

  return (
    <ControlPanel
      bare={bare}
      title="Group"
      count={facts.count}
      search={{ value: query, onChange: setQuery, placeholder: 'Find a column' }}
      onBack={onBack}
      {...(facts.reset
        ? { onReset: facts.reset.run, resetLabel: facts.reset.label }
        : {})}
      hint="Click again to ungroup"
    >
      <PanelSection label="Group rows by" first>
        {groupable.map((field) => (
          <PanelRow
            key={field.name}
            field={field}
            selected={list.view.group === field.name}
            onClick={() => list.setGroup(
              list.view.group === field.name ? undefined : field.name)}
          />
        ))}
        {groupable.length === 0 && (
          <PanelRow label="No columns match." />
        )}
      </PanelSection>
    </ControlPanel>
  )
}

/** Clicking a chosen column turns it round, then off. */
function SortPicker(
  { list, onBack, bare }: { list: UseListResult; onBack: () => void; bare?: boolean },
) {
  const facts = panelFacts('sort', list)
  const { query, setQuery, matches } = usePanelSearch()
  const sortable = list.fields.filter((field) => field.sortable)
  const sorted = list.view.sort
    .map((spec) => sortable.find((field) => field.name === spec.field))
    .filter((field): field is ListFieldDef => Boolean(field))
  const rest = sortable
    .filter((field) => !list.view.sort.some((spec) => spec.field === field.name))
    .filter(matches)

  const arrow = (name: string) => {
    const index = list.view.sort.findIndex((spec) => spec.field === name)
    if (index < 0) return undefined
    const spec = list.view.sort[index]!
    // The "2" the spec asks for on a secondary sort, said in words here
    // because a menu row has room for them.
    return `${spec.direction === 'asc' ? 'A → Z' : 'Z → A'}${
      list.view.sort.length > 1 ? ` · ${index + 1}` : ''}`
  }

  return (
    <ControlPanel
      bare={bare}
      title="Sort"
      count={facts.count}
      search={{ value: query, onChange: setQuery, placeholder: 'Find a column' }}
      onBack={onBack}
      {...(facts.reset
        ? { onReset: facts.reset.run, resetLabel: facts.reset.label }
        : {})}
      hint="Click again to reverse, then clear"
    >
      {sorted.length > 0 && (
        <PanelSection label="Sorted by" first>
          {sorted.map((field) => (
            <PanelRow
              key={field.name}
              field={field}
              detail={arrow(field.name)}
              selected
              onClick={() => list.toggleSort(field.name)}
            />
          ))}
        </PanelSection>
      )}

      <PanelSection label={sorted.length > 0 ? 'Add a column' : 'Sort by'} first={sorted.length === 0}>
        {rest.map((field) => (
          <PanelRow
            key={field.name}
            field={field}
            onClick={() => list.toggleSort(field.name, sorted.length > 0)}
          />
        ))}
        {rest.length === 0 && <PanelRow label="No columns match." />}
      </PanelSection>
    </ControlPanel>
  )
}

/** The Σ function per numeric column, shown in group rows and the footer. */
function SummaryPicker(
  { list, onBack, bare }: { list: UseListResult; onBack: () => void; bare?: boolean },
) {
  const facts = panelFacts('sum', list)
  const { query, setQuery, matches } = usePanelSearch()
  const aggregatable = list.fields.filter((field) => field.aggregatable).filter(matches)

  return (
    <ControlPanel
      bare={bare}
      title="Summarize"
      count={facts.count}
      search={{ value: query, onChange: setQuery, placeholder: 'Find a column' }}
      onBack={onBack}
      {...(facts.reset
        ? { onReset: facts.reset.run, resetLabel: facts.reset.label }
        : {})}
      hint="Shown in group rows and the footer"
    >
      <PanelSection label="Totals row" first>
        {aggregatable.map((field) => {
          const spec = list.view.aggregates.find((entry) => entry.field === field.name)
          return (
            <PanelRow key={field.name} field={field}>
              <MenuSelect
                label={`Summarize ${field.label}`}
                value={spec?.fn ?? null}
                onChange={(value) => list.setAggregate(
                  field.name, value as AggregateFunction | null)}
                options={AGGREGATE_FUNCTIONS.map((fn) => ({ value: fn, label: FUNCTION_LABELS[fn] }))}
                clearable
                clearLabel="No total"
                placeholder="—"
                minWidth={160}
                size="small"
                className="w-[96px] shrink-0"
              />
            </PanelRow>
          )
        })}
        {aggregatable.length === 0 && (
          <PanelRow label="No numeric columns on this list." />
        )}
      </PanelSection>
    </ControlPanel>
  )
}

// ---------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------

/**
 * One control panel, two shapes — a popover on a desktop, a sheet on a phone.
 *
 * Design system §6: *on a phone every panel is the same bottom sheet*. The
 * gear's five panels were each a `Popover` hung off a 32px button, which on a
 * 390px screen has nowhere to hang: List settings arrived as a floating card
 * in the middle of the page while Filter — one button to its left — came up
 * from the bottom with a grab handle and a sticky footer. Two surfaces, one
 * toolbar, no reason.
 *
 * The **body is the same component either way**, which is the point: the two
 * cannot drift because there is only one of them. What differs is the frame
 * around it, and that is decided here rather than in five places.
 *
 * `ControlPanel`'s own header carries the title and the back chevron on a
 * desktop; in a sheet the header belongs to the sheet (it is 56px, it holds
 * the grab handle and the ✕ a thumb can hit), so the body is handed `bare`
 * and renders its rows and its search well and nothing else. Reset comes up
 * here for the same reason — in a sheet it belongs in the footer beside Done,
 * and the footer is the sheet's.
 */
function ToolPanel({
  open, onClose, title, hint, sub, anchor, onBack, onReset, resetLabel, shell, children,
}: {
  open: boolean
  onClose: () => void
  title: string
  hint?: string
  /** What the panel currently says — "6 of 27 shown". Board 6 draws it under
      the title in a sheet; a popover puts it on the right of the header. */
  sub?: string
  /** The gear, for `placeFloating` on a desktop. Ignored on a phone. */
  anchor: HTMLElement | null
  /** Set on a second-level panel; the sheet draws `‹ List settings`. */
  onBack?: () => void
  /**
   * Resets whatever this panel decides, and the word for it.
   *
   * On a desktop it is `ControlPanel`'s own footer link, inside the body. In
   * a sheet the body has no footer — board 6 puts it beside Done, where a
   * thumb is already going — so the panel is handed it here and puts it in
   * whichever footer it has.
   */
  onReset?: () => void
  resetLabel?: string
  /**
   * Wrap the body in `ControlPanel` on a desktop.
   *
   * True for the gear's root, whose body is bare rows. The four pickers
   * bring their own `ControlPanel` — it is what carries their count, their
   * search box and their Reset — so wrapping those would draw two headers,
   * which is what this was doing the first time round.
   */
  shell?: boolean
  children: ReactNode
}) {
  const phone = usePhone()

  if (phone) {
    return (
      <PanelSheet
        open={open}
        onClose={onClose}
        title={title}
        {...(sub ?? hint ? { sub: sub ?? hint } : {})}
        {...(onBack ? { onBack, backLabel: 'List settings' } : {})}
        footer={
          <>
            {onReset && (
              <button
                type="button"
                onClick={onReset}
                className="shrink-0 text-body font-semibold text-accent"
              >
                {resetLabel ?? 'Reset'}
              </button>
            )}
            <SheetButton onClick={onClose}>
              <IconCheck size={16} />
              Done
            </SheetButton>
          </>
        }
      >
        {children}
      </PanelSheet>
    )
  }

  return (
    <Popover
      open={open}
      onClose={onClose}
      label={title}
      align="right"
      width={PANEL_WIDTH}
      anchor={anchor}
    >
      {shell
        ? (
          <ControlPanel
            title={title}
            {...(sub ? { count: sub } : {})}
            {...(hint ? { hint } : {})}
            {...(onBack ? { onBack } : {})}
            {...(onReset ? { onReset } : {})}
            {...(resetLabel ? { resetLabel } : {})}
          >
            {children}
          </ControlPanel>
        )
        : children}
    </Popover>
  )
}

export function ListTools({
  list, onExport, onImport, onNew, related, onAdvanced, squash,
}: {
  list: UseListResult
  onExport?: () => void
  onImport?: () => void
  /** The screen's primary action — "New job". v2 moves it out of the page bar. */
  onNew?: ReactNode
  /**
   * A list inside a record page.
   *
   * There is no room beside a property's jobs for a toolbar, and no appetite
   * for one: nobody imports into it or exports it. So everything rolls into
   * this one button — the four settings panels *and* the filter, which on a
   * full screen is its own control beside them — and the button is 28px and
   * sits in the card's header.
   */
  related?: boolean
  /** Opens the advanced builder, when the filter panel is in here. */
  onAdvanced?: () => void
  /*
   * `wordless` and `folded` are gone with the two buttons they described.
   * Smart import and Export are rows in this menu at every width that offers
   * them, so there is no rung at which they lose their words or move.
   */
  /**
   * Narrowest: the page's own actions are 36px squares too.
   *
   * This is the one rung where a screen's `onNew` — a node this component is
   * handed and cannot restyle from the inside — has to be squashed by a
   * selector rather than a prop, so it is done on the wrapper around *only*
   * that node.
   *
   * It used to be done on a wrapper around this whole component, and a
   * descendant selector does not know what a popover is: every row of the
   * gear menu, and of Sort, Group, Fields and Summarize, is a `<button>` with
   * an icon in it, so every one of them became 36px wide with no gap and no
   * padding. That is what Aaron saw — "smart import wraps to two lines", "the
   * labels are too close to their icons", "sort doesn't show the field names,
   * cuts all of them off". A row squeezed to 36px lays its label out at
   * min-content, which is one word, or two letters and an ellipsis.
   */
  squash?: boolean
}) {
  const [open, setOpen] = useState<GearPanel>(null)
  const conditions = list.view.filter?.conditions ?? []
  /*
   * Every panel behind this button is placed from the button, always.
   *
   * A related list's card is `overflow: hidden`, so its panels have to float
   * over everything rather than be cut off at the card's bottom edge — that
   * was the original reason, and it was applied only there. But the other
   * half of what `placeFloating` does is keep a panel on the screen, and a
   * standalone list wants that just as much: the gear sits 24px from the
   * right-hand edge of a phone, and a 304px panel hung from it with
   * `absolute right-0` is only on screen because 304 happens to fit. One
   * placement, so there is one thing to be right.
   */
  const gear = useRef<HTMLDivElement>(null)
  const anchor = gear.current

  /*
   * **Smart import and Export live in this menu, full stop** (Aaron).
   *
   * They used to be two more buttons on the toolbar, folding in here only at
   * the narrow rungs — so the toolbar carried four controls on a wide screen
   * and two on a medium one, and where a thing lived depended on the window.
   * They are the two things on a list somebody does once a quarter, and this
   * menu is already where the other five things a list can do are kept.
   *
   * On a phone they are not offered at all, and neither is Row height: a CSV
   * is not something anybody does from a phone, and three row heights is a
   * desktop's choice about a table that a phone does not draw.
   */
  const phone = usePhone()
  /*
   * The reset for whichever panel is open, which is all five panels' reset:
   * only one of them is ever open, so the four that are closed are handed a
   * value they do not draw.
   */
  const facts = panelFacts(open, list)

  return (
    <div className="flex items-center gap-2">
      {/* The ref goes here rather than on the Button: every panel inside is
          absolute or fixed, so this wrapper's box is the button's box. */}
      <div ref={gear} className="relative">
        <Button
          onClick={() => setOpen((current) => (current ? null : 'gear'))}
          aria-expanded={open !== null}
          aria-label="List settings"
          title="List settings"
          size={related ? 'default' : 'toolbar'}
          variant={open ? 'pressed' : 'default'}
          square={!related}
          className={related ? 'h-7 px-1.5' : undefined}
        >
          {/*
            * `SQUARE_GLYPH`, because the gear is the toolbar button that
            * never has a word beside it: its glyph is the whole control. The
            * hand-tuned 17 it used to carry was an attempt at the same idea,
            * and it never matched the funnel because the real problem was
            * that the button's padding was squashing it to 10px wide.
            */}
          <IconSettings size={related ? TOOLBAR_GLYPH : SQUARE_GLYPH} />
          {related && conditions.length > 0 && (
            <span className="tnum rounded-chip bg-accent-soft px-1 text-tag font-semibold text-accent">
              {conditions.length}
            </span>
          )}
        </Button>

        <ToolPanel
          open={open === 'gear'}
          onClose={() => setOpen(null)}
          title="List settings"
          hint="Applies to this view"
          anchor={anchor}
          shell
        >
          <SettingsRoot
            list={list}
            related={related}
            onImport={onImport}
            onExport={onExport}
            onOpen={setOpen}
          />
        </ToolPanel>

        <ToolPanel
          open={open === 'group'}
          onClose={() => setOpen(null)}
          title="Group"
          anchor={anchor}
          sub={facts.count}
          {...(facts.reset
        ? { onReset: facts.reset.run, resetLabel: facts.reset.label }
        : {})}
          onBack={() => setOpen('gear')}
        >
          <GroupPicker
            list={list}
            onBack={() => setOpen('gear')}
            bare={phone}
          />
        </ToolPanel>

        <ToolPanel
          open={open === 'sort'}
          onClose={() => setOpen(null)}
          title="Sort"
          anchor={anchor}
          sub={facts.count}
          {...(facts.reset
        ? { onReset: facts.reset.run, resetLabel: facts.reset.label }
        : {})}
          onBack={() => setOpen('gear')}
        >
          <SortPicker
            list={list}
            onBack={() => setOpen('gear')}
            bare={phone}
          />
        </ToolPanel>

        <ToolPanel
          open={open === 'fields'}
          onClose={() => setOpen(null)}
          title="Fields"
          anchor={anchor}
          sub={facts.count}
          {...(facts.reset
        ? { onReset: facts.reset.run, resetLabel: facts.reset.label }
        : {})}
          onBack={() => setOpen('gear')}
        >
          <FieldsPanel
            list={list}
            onBack={() => setOpen('gear')}
            bare={phone}
          />
        </ToolPanel>

        <ToolPanel
          open={open === 'sum'}
          onClose={() => setOpen(null)}
          title="Summarize"
          anchor={anchor}
          sub={facts.count}
          {...(facts.reset
        ? { onReset: facts.reset.run, resetLabel: facts.reset.label }
        : {})}
          onBack={() => setOpen('gear')}
        >
          <SummaryPicker
            list={list}
            onBack={() => setOpen('gear')}
            bare={phone}
          />
        </ToolPanel>

        {/*
          * A related list's Filter is the same popover, hung from the gear.
          * The only difference on a card is that it is behind the button
          * rather than beside it — the control itself is the same one, so
          * "filter this list" means one thing wherever it is asked.
          */}
        <QuickFilter
          list={list}
          open={open === 'filter'}
          onClose={() => setOpen(null)}
          anchor={anchor}
          onAdvanced={() => { setOpen(null); onAdvanced?.() }}
        />
      </div>

      {/*
        * The screen's primary action comes from the page, as its own <Button>,
        * so the one place that can promise it is the same height as the row it
        * sits in is the row. `display: contents` leaves the layout alone.
        *
        * `squash` adds the phone rung's 36px square. `:has(svg)`, because a
        * button with no glyph would become an empty white box: a control with
        * no name, no icon and no way to guess what it does. **So an action on
        * a list toolbar carries a glyph.** Equipment's "Morning assignment"
        * and Invoicing's "Settings" and "QuickBooks" did not, and kept their
        * full width on a phone — which pushed the green button past the
        * right-hand edge of the screen. They have one now, and a new screen
        * adding a word-only action here will do the same thing again.
        */}
      {onNew && (
        <span className={cn('contents [&_button]:h-9', squash
          && '[&_button:has(svg)]:w-9 [&_button:has(svg)]:gap-0 [&_button:has(svg)]:px-0'
          + ' [&_button:has(svg)]:text-[0px]'
          /*
           * And the glyph grows to `SQUARE_GLYPH`, like the two buttons
           * beside it. The page hands this node over already built, so its
           * icon's own `size` cannot be reached from here — but the svg is a
           * descendant, and width and height in CSS beat the attributes
           * lucide writes.
           */
          + ' [&_button:has(svg)_svg]:h-[18px] [&_button:has(svg)_svg]:w-[18px]')}
        >
          {onNew}
        </span>
      )}
    </div>
  )
}

/**
 * The body of List settings — the gear's root, in a popover or in a sheet.
 *
 * It was written inline inside `ListTools`, which is why the phone never had
 * it: the popover and the sheet would each have needed a copy, and a copy is
 * what §6.3 exists to prevent. Out here it is one component, and the only
 * thing either shape decides is how tall its rows are.
 */
function SettingsRoot({
  list, related, onImport, onExport, onOpen,
}: {
  list: UseListResult
  related?: boolean
  onImport?: () => void
  onExport?: () => void
  onOpen: (panel: GearPanel) => void
}) {
  const phone = usePhone()

  /*
   * No padding at either width now: every row carries its own full-bleed rule
   * (`GearRow`), the way the quick filter's sections do, and a 6px gutter
   * would stop the rules short of the menu's edges and give away that these
   * are two different menus.
   */
  return (
    <ul className="flex list-none flex-col p-0">
      {/* Filter is a control of its own on a full screen; here it is
          one more row behind the same button. */}
      {related && (
        <GearRow
          big={phone}
          icon={IconFilter}
          label="Filter"
          value={panelFacts('filter', list).value}
          onClick={() => onOpen('filter')}
        />
      )}
      <GearRow
        big={phone}
        icon={IconGroup}
        label="Group"
        value={panelFacts('group', list).value}
        onClick={() => onOpen('group')}
      />
      <GearRow
        big={phone}
        icon={IconSort}
        label="Sort"
        value={panelFacts('sort', list).value}
        onClick={() => onOpen('sort')}
      />
      <GearRow
        big={phone}
        icon={IconFields}
        label="Fields"
        value={panelFacts('fields', list).value}
        onClick={() => onOpen('fields')}
      />
      <GearRow
        big={phone}
        icon={IconSigma}
        label="Summarize"
        value={panelFacts('sum', list).value}
        onClick={() => onOpen('sum')}
      />

      {/*
        * **Smart import and Export, here and nowhere else** (Aaron) — and
        * **not on a phone at all**.
        *
        * They were two more buttons on the toolbar that folded in here only
        * at the narrow rungs, so where they lived depended on the window.
        * They are the two things a list does once a quarter and this is
        * already where its other five live.
        *
        * A phone is not offered them: picking a CSV out of a downloads
        * folder and mapping thirty columns is not something anybody does
        * standing up, and an action that cannot be finished is worse on the
        * menu than off it.
        */}
      {!phone && (onImport || onExport) && (
        <>
          {onImport && (
            <GearRow
              icon={IconSmartImport}
              label="Smart import"
              onClick={() => { onOpen(null); onImport() }}
            />
          )}
          {onExport && (
            <GearRow
              icon={IconExport}
              label="Export"
              value="This view"
              onClick={() => { onOpen(null); onExport() }}
            />
          )}
        </>
      )}

      {/*
        * Keeping the settings, for a list with no tab row to save a view
        * in. Two audiences because the two are different jobs: an owner
        * decides what a property's jobs list looks like for the company,
        * and anybody can keep their own on top of that.
        */}
      {related && (
        <li className={phone
          ? 'border-t-8 border-bg px-4 pb-1 pt-3'
          : 'px-3.5 pb-1 pt-2.5'}
        >
          <SectionLabel className="mb-1.5 block">Keep these settings</SectionLabel>
          <div className="flex items-center gap-1.5">
            <Button
              size={phone ? 'toolbar' : 'default'}
              className={phone ? 'flex-1 justify-center' : 'h-7 flex-1'}
              onClick={() => void list.rememberView('me')}
            >
              For me
            </Button>
            <Button
              size={phone ? 'toolbar' : 'default'}
              className={phone ? 'flex-1 justify-center' : 'h-7 flex-1'}
              onClick={() => void list.rememberView('everyone')}
            >
              For everyone
            </Button>
          </div>
          {list.rememberedMine && (
            <button
              type="button"
              onClick={() => void list.forgetView()}
              className="mt-1.5 text-sub text-accent hover:text-accent-dark"
            >
              Use the company’s instead
            </button>
          )}
        </li>
      )}

      {/*
        * **Row height, on a desktop only** (Aaron).
        *
        * It is a choice about a table: three heights for the rows of a grid
        * somebody is scanning across. A phone draws cards, which have one
        * height and get it from their own content, so the control was three
        * tiles that changed nothing you could see. Board 6's `DensityTiles`
        * goes with it — it existed to make this control usable at a width
        * where it should not have been offered.
        *
        * It keeps its own label and its own rule, because unlike the rows
        * above it this is a control rather than a way through to one.
        */}
      {!phone && (
        <li className="px-3.5 pb-3 pt-2.5">
          <SectionLabel className="mb-1.5 block">Row height</SectionLabel>
          <Segmented
            full
            label="Row height"
            value={list.view.density}
            onChange={(value) => list.setDensity(value)}
            options={[
              { value: 'compact' as const, label: 'Compact', glyph: <DensityLines gap={3} /> },
              { value: 'default' as const, label: 'Comfortable', glyph: <DensityLines gap={5} /> },
              { value: 'spacious' as const, label: 'Spacious', glyph: <DensityLines gap={7} /> },
            ]}
          />
        </li>
      )}
    </ul>
  )
}



/** Sentence case, the way §3.8 asks — not the wire's "sum" and "avg". */
const FUNCTION_LABELS: Record<AggregateFunction, string> = {
  sum: 'Sum', avg: 'Average', min: 'Minimum', max: 'Maximum', count: 'Count',
}


/** Three rules `gap` pixels apart — the row height, drawn. */
function DensityLines({ gap }: { gap: number }) {
  const height = gap * 2 + 3
  return (
    <svg
      aria-hidden
      width={20}
      height={17}
      viewBox={`0 -${(17 - height) / 2} 20 17`}
      className="text-current"
    >
      {[0, gap, gap * 2].map((y) => (
        <line key={y} x1="2" x2="18" y1={y + 1.5} y2={y + 1.5}
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
      ))}
    </svg>
  )
}

/**
 * One row of List settings, at the two heights it is ever drawn at.
 *
 * `big` is board 6's 52px phone row — an 18px glyph, the label at 15/500,
 * the current answer at 13.5 muted and a chevron, because a row that leads
 * somewhere should say so. The popover's row is 28px with a 14px glyph and
 * no chevron, since a mouse does not need the target and a 304px panel does
 * not have the width to spare.
 *
 * The icon arrives as the **component**, not as an element, so this decides
 * its size rather than each of the eight call sites deciding it separately —
 * the same rule as `ControlButton` above, and for the same reason.
 */
function GearRow({
  icon: Icon, label, value, onClick, big,
}: {
  icon: (props: { size?: number }) => ReactElement
  label: string
  value?: string
  onClick: () => void
  big?: boolean
}) {
  if (big) {
    return (
      <li>
        <button
          type="button"
          onClick={onClick}
          className="flex h-[52px] w-full items-center gap-3 border-b border-line-faint px-4 text-left active:bg-hover"
        >
          <span aria-hidden className="shrink-0 text-text-muted"><Icon size={18} /></span>
          <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-text">{label}</span>
          {value && (
            <span className="max-w-[150px] truncate text-[13.5px] text-text-muted">{value}</span>
          )}
          <span aria-hidden className="shrink-0 text-text-faint">
            <IconChevronRight size={16} />
          </span>
        </button>
      </li>
    )
  }

  /*
   * **The quick filter's row, to the pixel** (Aaron).
   *
   * These two menus hang off buttons one inch apart on the same toolbar, and
   * they were drawn to two different recipes: this one a 29px rounded button
   * with a 12.5px regular label and no rule, the filter's a 40px row with a
   * 13px bold label and a hairline under it. Nothing was broken — they simply
   * did not read as the same product's menus, which is the only thing a pair
   * of controls that close together has to do.
   *
   * So: `h-10`, `px-3.5`, `gap-2`, a 14px muted glyph, the label at
   * `text-body font-bold`, the value right-aligned at `text-sub`, a 13px
   * chevron, and `border-b border-line-faint` — every one of them taken from
   * `quick-filter.tsx`'s `Section`. The chevron is the one addition, because
   * unlike a filter section these rows go somewhere rather than open in
   * place, and `ToolPanel` draws the way back.
   */
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn('flex h-10 w-full items-center gap-2 border-b border-line-faint px-3.5',
          'text-left hover:bg-hover')}
      >
        <span aria-hidden className="shrink-0 text-text-muted"><Icon size={14} /></span>
        {/* The label takes the slack, so the value and the chevron are always
            hard right whether or not there is a value to draw. */}
        <span className="min-w-0 flex-1 truncate text-body font-bold text-text">{label}</span>
        {value && (
          <span className="shrink-0 truncate whitespace-nowrap text-sub font-medium text-text-faint">
            {value}
          </span>
        )}
        <span aria-hidden className="shrink-0 text-text-faint">
          <IconChevronRight size={13} />
        </span>
      </button>
    </li>
  )
}

/**
 * What the list is filtered by — a chip per condition, naming records rather
 * than ids (v3 §4.5).
 *
 * It was one grey sentence and a green "Clear all", which was the right
 * answer to the problem it replaced (six chips that wrapped and pushed the
 * toolbar sideways) and the wrong answer to the question people ask of this
 * row: *which* of these do I want rid of? A sentence has one × and it clears
 * everything.
 *
 * So: chips again, but **at most two and then a "+N"**, which is what stops
 * the wrapping without losing the per-condition ×. The group is `min-w-0
 * shrink overflow-hidden` — it can lose chips to the "+N", it can never wrap
 * and never push the buttons. Clear is a text link at the end, not a chip,
 * because it is not one of the things being listed.
 */
export function FilterChips({
  list, onOpenField,
}: {
  list: UseListResult
  onOpenField?: (field: string) => void
}) {
  const nodes = list.view.filter?.conditions ?? []
  if (nodes.length === 0) return null

  /*
   * Two, and the rest behind a number. With a selection showing, one — the
   * selection slot is in the same row and is the more useful of the two
   * things competing for it (§2.4).
   */
  const shown = nodes.slice(0, list.selectedCount > 0 ? 1 : 2)
  const hidden = nodes.length - shown.length

  /*
   * **The chips are what gets cut, never Clear** (Aaron: "when multiple
   * filters are applied, their chips push the clear behind the bulk actions
   * button").
   *
   * This was one `overflow-hidden` box holding the chips, the `+N` and the
   * Clear, all of them `shrink-0`. Nothing inside could give way, so the row
   * overflowed and the box clipped it — and what sits at the clipped end is
   * whatever is last, which is Clear. So the one control that undoes the
   * thing taking up the room was the one the room ran out on, and it looked
   * as though the Bulk actions button beside it had covered it.
   *
   * Two boxes now: the chips scroll out of their own `overflow-hidden`, and
   * Clear sits outside it as a sibling that cannot be reached by the clip. A
   * chip that is too long already truncates its own label at 160px, so what
   * is lost is the end of a value rather than the way out.
   */
  return (
    <div className="flex min-w-0 shrink items-center gap-1.5">
      <div className="flex min-w-0 shrink items-center gap-1.5 overflow-hidden">
        {shown.map((node, index) => (
          <FilterChip
            key={index}
            list={list}
            node={node}
            onOpen={onOpenField}
            onRemove={() => list.setFilter(nodes.filter((other) => other !== node))}
          />
        ))}

        {hidden > 0 && (
          <button
            type="button"
            onClick={() => onOpenField?.(firstFieldOf(nodes[shown.length]!))}
            title={describeFilter({ match: 'and', conditions: nodes }, filterLabels(list))}
            className="flex h-7 shrink-0 items-center rounded-chip border border-line bg-surface px-2.5 text-[12px] text-text-muted"
          >
            +{hidden}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => list.setFilter([])}
        className="shrink-0 text-cell font-semibold text-accent hover:text-accent-dark"
      >
        Clear
      </button>
    </div>
  )
}

/**
 * One condition, as `<field muted> <value bold>`.
 *
 * The **value without the field name** — `Meridian Property Group +1`, not
 * "Company is any of Meridian Property Group and Blue Ridge Country Club" —
 * because the field name is already the first half of the chip and reading it
 * twice is how a chip grows to 300px. The operator is spelled only when it is
 * not the obvious one: `not Residential`, `is empty`.
 *
 * The glyph is §5.4's: a **clock** when the window moves with today, a
 * **lock** when the dates are fixed. That difference is invisible in the text
 * — "Aug 23 – Sep 21" and "Last 30 days" are the same fortnight today and
 * different ones next month — and it is the single most surprising thing
 * about a saved view, so it gets a mark rather than a footnote.
 */
function FilterChip({
  list, node, onOpen, onRemove, tall,
}: {
  list: UseListResult
  node: FilterNode
  onOpen?: ((field: string) => void) | undefined
  onRemove: () => void
  /** 32px on a phone's own strip, 28px in the desktop toolbar (§7b). */
  tall?: boolean
}) {
  const labels = filterLabels(list)
  const full = isFilterGroup(node) || isRelatedCondition(node)
    ? describeFilter({ match: 'and', conditions: [node] }, labels)
    : describeCondition(node as FilterCondition, labels)

  /*
   * A group or a related condition has no one field and no short form worth
   * inventing — "has a visit where (…)" *is* the chip. It still removes.
   */
  if (isFilterGroup(node) || isRelatedCondition(node)) {
    return <ChipShell title={full} tall={tall} onRemove={onRemove}>{full}</ChipShell>
  }

  const condition = node as FilterCondition
  const field = list.fields.find((one) => one.name === condition.field)
  const values = Array.isArray(condition.value) ? condition.value : [condition.value]
  const first = values[0] === undefined ? '' : String(labels.value(values[0], condition))
  const more = values.length - 1

  const nullary = condition.op === 'is_null' || condition.op === 'is_not_null'
  const plain = condition.op === 'in' || condition.op === 'eq' || condition.op === 'in_period'
    || condition.op === 'in_relative'

  /*
   * **A record this person cannot reach gets a lock, not a gap** (§4.4).
   *
   * `labelFor` answers `null` only when the repair lookup came back and that
   * id was not in it, which is the one case where the chip is naming a record
   * outside the viewer's branches. The filter still applies — the condition
   * is not a permission — so the chip stays, reads "a company you can't see"
   * and says why by its shape rather than by a sentence beside it.
   *
   * It cannot fire for an id the condition already labels: the lookup skips
   * those, because a name written down when somebody picked it is the better
   * answer and costs no request. So this is the saved-view case the repair
   * exists for, which is the one it was asked to cover.
   */
  const unseen = values.some((one) =>
    typeof one === 'string' && labelFor(condition, one, list.labelCache) === null)

  /*
   * The hover text is the **whole** readback, one line per value, and the
   * accessible name with it — a 160px chip truncates, and "Meridian Prope…"
   * with no way to see the rest is worse than a long chip. §5.4's sentence
   * rides along on a date, because clock-versus-lock is a difference nobody
   * guesses from a glyph the first time they meet it.
   *
   * A native `title` rather than a dark tooltip of our own: the words are
   * what matter here, and a hover card is a design-system component with
   * positioning, delay and a touch story of its own, not a detail of this
   * chip. Noted in the design system as the one place that still wants one.
   */
  const lines = [full, ...(values.length > 1
    ? values.map((one) => String(labels.value(one, condition)))
    : [])]
  if (condition.op === 'in_relative') lines.push('Moves with today.')
  if (condition.op === 'between') lines.push('Fixed dates.')
  if (condition.op === 'in_period') {
    const period = datePeriod.safeParse(condition.value)
    if (period.success) {
      lines.push('preset' in period.data ? 'Moves with today.' : 'Fixed dates.')
    }
  }
  if (unseen) lines.push('Outside the branches you can see. The filter still applies.')

  return (
    <ChipShell
      title={lines.join('\n')}
      label={full}
      glyph={unseen ? <IconLock size={11} /> : dateGlyph(condition)}
      muted={unseen}
      tall={tall}
      onRemove={onRemove}
      {...(onOpen ? { onClick: () => onOpen(condition.field) } : {})}
    >
      <span className="text-text-muted">{field?.label ?? condition.field}</span>
      {' '}
      <b className="font-semibold text-text">
        {nullary
          ? (condition.op === 'is_null' ? 'is empty' : 'is not empty')
          : `${plain ? '' : `${OPERATOR_SHORT[condition.op] ?? ''} `}${first}`}
      </b>
      {more > 0 && <span className="text-text-faint"> +{more}</span>}
    </ChipShell>
  )
}

/** The 28px box every chip wears, so the three kinds cannot drift apart. */
function ChipShell({
  children, title, label, glyph, muted, tall, onClick, onRemove,
}: {
  children: ReactNode
  /** The hover text, which may be several lines. */
  title: string
  /** The one-line accessible name; the title when it is already one line. */
  label?: string
  glyph?: ReactNode
  /** A record outside the viewer's branches: dashed and quiet, never absent. */
  muted?: boolean
  /** 32px rather than 28px, which is the board's chip height on a phone. */
  tall?: boolean
  onClick?: () => void
  onRemove: () => void
}) {
  return (
    <span
      title={title}
      className={cn('flex shrink-0 items-center gap-1 rounded-chip border',
        'bg-surface pl-2.5 pr-1 text-[12px]',
        tall ? 'h-8' : 'h-7',
        muted ? 'border-dashed border-line text-text-muted' : 'border-line')}
    >
      {glyph && (
        <span aria-hidden className={cn('shrink-0', muted ? 'text-text-faint' : 'text-accent')}>
          {glyph}
        </span>
      )}
      <button
        type="button"
        onClick={onClick}
        disabled={!onClick}
        aria-label={label ?? title}
        className={cn('truncate text-left disabled:cursor-default',
          tall ? 'max-w-[220px]' : 'max-w-[160px]')}
      >
        {children}
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label ?? title}`}
        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-chip text-text-faint hover:bg-line-soft hover:text-text"
      >
        <IconClose size={10} />
      </button>
    </span>
  )
}

/**
 * Clock or lock, on a date condition and nothing else (§5.4).
 *
 * A window that moves with today and one pinned to two dates read the same
 * on the day they are made and differently a month later, which is the one
 * thing about a saved view people are surprised by. Nothing for an empty
 * check: "is empty" is neither.
 */
function dateGlyph(condition: FilterCondition): ReactNode {
  if (condition.op === 'in_relative') return <IconClock size={11} />
  // A token is the most relative value there is: `today` is resolved when
  // the query runs, so "Due < today" means today on whatever day it is read.
  if (isToken(condition.value)) return <IconClock size={11} />
  if (condition.op === 'in_period') {
    const period = datePeriod.safeParse(condition.value)
    if (!period.success) return null
    return 'preset' in period.data
      ? <IconClock size={11} />
      : <IconLock size={11} />
  }
  if (condition.op === 'between') return <IconLock size={11} />
  return null
}

/** The word a chip spells when the operator is not the obvious one. */
const OPERATOR_SHORT: Partial<Record<FilterOperator, string>> = {
  neq: 'not',
  not_in: 'not',
  gt: '>',
  gte: '\u2265',
  lt: '<',
  lte: '\u2264',
  contains: 'contains',
  starts_with: 'starts with',
}

/** Which section the "+N" chip should open the filter on. */
function firstFieldOf(node: FilterNode): string {
  if (isFilterGroup(node)) {
    const inner = node.conditions[0]
    return inner ? firstFieldOf(inner) : ''
  }
  if (isRelatedCondition(node)) return ''
  return (node as FilterCondition).field
}

/**
 * The filter chips on a phone — their own line, under the toolbar, and only
 * while there are any (v3 §7b).
 *
 * The toolbar row at 390px is a search box and three 36px buttons, and that
 * is the whole of it. `FilterChips` used to be in that row as a `flex-1`
 * item, so three conditions took the width off the search box and then
 * pushed the green New button off the right-hand edge of the screen.
 *
 * Out here it is a strip that scrolls sideways while the page does not:
 * `-mx-4 px-4` so a chip can run to the physical edge rather than stopping
 * at the page gutter — which is what makes it *look* scrollable — and the
 * count rides along, because the count is what the filter changed and the
 * row above has nowhere to put it.
 *
 * **Clear is pinned**, over a fade. A chip strip that scrolls is a strip
 * whose last chip you may never see; the one control somebody wants from it
 * when they have lost track is the way out, so the way out does not scroll.
 */
export function PhoneFilterStrip({
  list, onOpenField,
}: {
  list: UseListResult
  onOpenField?: (field: string) => void
}) {
  const nodes = list.view.filter?.conditions ?? []
  if (nodes.length === 0) return null

  return (
    <div className="relative -mx-4">
      <div className="no-scrollbar flex items-center gap-2 overflow-x-auto px-4 pb-2.5">
        <span className="tnum shrink-0 text-cell text-text-muted">
          {formatNumber(list.visibleCount)} of {formatNumber(list.totalCount)}
        </span>

        {/*
          * **Chips, one per condition — the same ones the toolbar draws.**
          *
          * This was the sentence, which is the right shape for a desktop
          * toolbar that has to fit beside six controls and the wrong one
          * here: a sentence has one ×, so the only thing a phone could do to
          * a filter of three conditions was clear all three. The strip
          * scrolls sideways and the page does not, which is why there is no
          * "+N" — every condition is on it, each with its own ×, and nothing
          * hides behind a number (§7b).
          */}
        {nodes.map((node, index) => (
          <FilterChip
            key={index}
            list={list}
            node={node}
            tall
            onOpen={onOpenField}
            onRemove={() => list.setFilter(nodes.filter((other) => other !== node))}
          />
        ))}

        {/* Room for Clear to sit over, so the last chip is reachable. */}
        <span aria-hidden className="h-8 w-[96px] shrink-0" />
      </div>

      {/*
        * **Clear is a chip, and it is opaque.**
        *
        * It was bare green text over a fade, and a fade is transparent by
        * definition — so a chip scrolling underneath showed straight through
        * it and the two sets of words sat on top of each other. At the right
        * of a strip whose whole job is to scroll, the one control that never
        * moves has to be the one thing you can always read.
        *
        * So: the same 32px `rounded-chip` the conditions wear, in the
        * product's own green pair (accent on accent-soft, an accent-line
        * border) rather than a fifth shape invented here — and a solid block
        * of page behind it, with the fade reduced to the 20px to its left,
        * which is all a fade was ever for: saying there is more.
        */}
      <div className="pointer-events-none absolute bottom-2.5 right-0 flex h-8 items-center">
        <span aria-hidden className="h-8 w-5 bg-gradient-to-r from-transparent to-bg" />
        <span className="flex h-8 items-center bg-bg pl-1 pr-4">
          <button
            type="button"
            onClick={() => list.setFilter([])}
            className={cn('pointer-events-auto flex h-8 shrink-0 items-center rounded-chip',
              'border border-accent-line bg-accent-soft px-3',
              'text-[12px] font-semibold text-accent')}
          >
            Clear
          </button>
        </span>
      </div>
    </div>
  )
}
